import React, { useEffect, useState } from "react";
import Bugsnag from "@bugsnag/js";
import Modal from "../../../ui-elements/Modal/Modal";
import Button from "../../../ui-elements/Button/Button";
import Radio from "../../../ui-elements/Radio/Radio";
import Input from "../../../ui-elements/Input/Input";
import Alert, { AlertType } from "../../../ui-elements/Alert/Alert";
import { faTrash } from "@fortawesome/pro-solid-svg-icons";
import { GET_KELDAN_API_URL } from "../../../services/config";

type CustomerStatus = "active" | "inactive";

interface IProps {
    show: boolean;
    close: () => void;
    customerId: number;
    customerName: string;
    customerType: "company" | "individual";
    initialActive?: boolean;
    initialActiveDescription?: string | null;
    onStatusChanged?: (active: boolean, activeDescription: string | null) => void;
    onDeleted?: () => void;
    setToastShow: (show: boolean) => void;
    setToastType: (type: AlertType) => void;
    setToastMessage: (message: string) => void;
}

const EditCustomerStatusModal: React.FC<IProps> = ({
    show,
    close,
    customerId,
    customerName,
    customerType,
    initialActive = true,
    initialActiveDescription = null,
    onStatusChanged,
    onDeleted,
    setToastShow,
    setToastType,
    setToastMessage,
}) => {
    const [status, setStatus] = useState<CustomerStatus>(initialActive ? "active" : "inactive");
    const [comment, setComment] = useState(initialActiveDescription ?? "");
    const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);
    const [disableSubmit, setDisableSubmit] = useState(false);

    useEffect(() => {
        setStatus(initialActive ? "active" : "inactive");
        setComment(initialActiveDescription ?? "");
    }, [initialActive, initialActiveDescription]);

    const handleStatusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setStatus(e.target.value as CustomerStatus);
    };

    const handleClose = () => {
        setStatus(initialActive ? "active" : "inactive");
        setComment(initialActiveDescription ?? "");
        setConfirmDeleteModalOpen(false);
        close();
    };

    const handleSubmit = async () => {
        const active = status === "active";
        const newActiveDescription = comment || null;
        if (active === initialActive && newActiveDescription === (initialActiveDescription ?? null)) {
            handleClose();
            return;
        }

        try {
            setDisableSubmit(true);
            const response = await fetch(`${GET_KELDAN_API_URL()}/DueDiligence/UpdateCustomerStatus`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    customerId,
                    active,
                    isIndividual: customerType === "individual",
                    comment,
                    activeDescription: comment || null,
                })
            });

            if (!response.ok) {
                const errorData = await response.text();
                setToastMessage(errorData || "Ekki tókst að uppfæra stöðu viðskiptavinar");
                setToastType("error");
                setToastShow(true);
                return;
            }

            setToastMessage("Viðskiptavini breytt");
            setToastType("success");
            setToastShow(true);
            onStatusChanged?.(active, comment || null);
            close();
        } catch (error) {
            if (error instanceof Error) Bugsnag.notify(error);
            setToastMessage("Ekki tókst að uppfæra stöðu viðskiptavinar");
            setToastType("error");
            setToastShow(true);
        } finally {
            setDisableSubmit(false);
        }
    };

    const handleDelete = async () => {
        try {
            setDisableSubmit(true);
            const endpoint = customerType === "individual"
                ? `${GET_KELDAN_API_URL()}/DueDiligence/RemoveIndividual/${customerId}`
                : `${GET_KELDAN_API_URL()}/DueDiligence/RemoveCustomer/${customerId}`;

            const response = await fetch(endpoint, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
                }
            });

            if (!response.ok) {
                const errorData = await response.text();
                setToastMessage(errorData || "Ekki tókst að eyða viðskiptavini");
                setToastType("error");
                setToastShow(true);
                return;
            }

            setToastMessage(customerType === "individual"
                ? "Einstaklingi eytt"
                : "Lögaðila eytt"
            );
            setToastType("success");
            setToastShow(true);
            setConfirmDeleteModalOpen(false);
            handleClose();
            onDeleted?.();
        } catch (error) {
            if (error instanceof Error) Bugsnag.notify(error);
            setToastMessage("Ekki tókst að eyða viðskiptavini");
            setToastType("error");
            setToastShow(true);
        } finally {
            setDisableSubmit(false);
        }
    };

    const renderBody = () => (
        <div className="KCL_edit-customer-status-modal">
            <div className="status-radio-group">
                <Radio
                    id="status-active"
                    name="customer-status"
                    value="active"
                    defaultValue={status}
                    onChange={handleStatusChange}
                >
                    Virkur
                </Radio>
                <Radio
                    id="status-inactive"
                    name="customer-status"
                    value="inactive"
                    defaultValue={status}
                    onChange={handleStatusChange}
                >
                    Óvirkur
                </Radio>
            </div>
            <Input
                inputType="textArea"
                placeholder="Athugasemdir"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
            />
            <div className="button-group">
                <div className="button-group__left">
                    <Button
                        buttonType="secondary"
                        onClick={handleClose}
                    >
                        Hætta við
                    </Button>
                    <Button
                        buttonType="primary"
                        onClick={handleSubmit}
                        disabled={disableSubmit}
                    >
                        Staðfesta
                    </Button>
                </div>
                <Button
                    buttonType="secondary"
                    onClick={() => setConfirmDeleteModalOpen(true)}
                    disabled={disableSubmit}
                    icon={faTrash}
                >
                    Eyða
                </Button>
            </div>
        </div>
    );

    return (
        <>
            <Modal
                show={show}
                onHide={handleClose}
                header="Breyta viðskiptavini"
                body={renderBody()}
                hasCloseButton
                className="edit-customer-status-modal"
            />
            <Modal
                className="confirm-delete-modal"
                show={confirmDeleteModalOpen}
                onHide={() => setConfirmDeleteModalOpen(false)}
                header="Eyða viðskiptavini"
                body={
                    <div className="alertBody">
                        <Alert
                            type="alert"
                            headText={`Þú ert að fara eyða ${customerName}.`}
                        />
                        <div className="infoSection">
                            <p className="paragraph">
                                Viltu halda áfram?
                            </p>
                        </div>
                        <div className="actions">
                            <Button onClick={() => setConfirmDeleteModalOpen(false)} buttonType="secondary" size="lg">Hætta við</Button>
                            <Button onClick={handleDelete} buttonType="primary" size="lg" disabled={disableSubmit}>Staðfesta</Button>
                        </div>
                    </div>
                }
            />
        </>
    );
};

export default EditCustomerStatusModal;
